// noinspection BadExpressionStatementJS

import { useMemo, type FC, type ReactNode } from 'react'
import { StyleSheet, type StyleProp, type ViewProps, type ViewStyle } from 'react-native'
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  type AnimatedStyle,
  type EasingFunction,
  type SharedValue
} from 'react-native-reanimated'
import { useLoop } from 'react-native-reanimated-hooks'

const defaultEasing = Easing.bezier(0.3, 0.01, 0.3, 0.15).factory()
const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row'
  }
})

// do NOT replace this with lodash.range!
function range(size: number) {
  'worklet'

  return Array(size).fill(0) as 0[]
}

function getLoopInterpolateInputRange({ count }: { count: number }): number[] {
  'worklet'

  return range(count + 1).map((_, idx) => idx / count)
}

const getLoopInterpolateOutputRange = ({
  count,
  calcRange
}: {
  calcRange: (positionIndex: number) => number
  count: number
}) => {
  'worklet'
  const outputRange = range(count).map((_, idx) => calcRange(idx))

  outputRange.unshift(outputRange.slice(-1)[0])

  return outputRange
}

type IndicatorEnableProps = {
  opacityEnabled?: boolean
  scaleEnabled?: boolean
}

type DotProps = {
  style: StyleProp<Animated.AnimateStyle<StyleProp<ViewStyle>>>
  index: number
  count: number
  value: SharedValue<number>
  scaleEnabled: boolean
  opacityEnabled: boolean
  inputRange: number[]
}

const Dot: FC<DotProps> = ({ style, index, count, value, scaleEnabled, opacityEnabled, inputRange }) => {
  const animStyle = useAnimatedStyle(() => {
    const count_m1 = count - 1
    const transform = []

    if (scaleEnabled) {
      transform.push({
        scale: interpolate(
          value.value,
          inputRange,
          getLoopInterpolateOutputRange({
            count,
            calcRange: (idx) => 1.0 - (0.46 / count_m1) * ((count_m1 + index - idx) % count)
          })
        )
      })
    }

    return {
      opacity: opacityEnabled
        ? interpolate(
            value.value,
            inputRange,
            getLoopInterpolateOutputRange({
              count,
              calcRange: (idx) => 1 - 0.55 * (((count_m1 + index - idx) % count) / count) ** 0.14
            })
          )
        : undefined,
      transform
    } satisfies AnimatedStyle<StyleProp<ViewStyle>>
  }, [])

  return <Animated.View style={[style, animStyle]} />
}

type DotIndicatorProps = ViewProps &
  IndicatorEnableProps & {
    animating?: boolean
    color?: string
    containerStyle?: StyleProp<ViewStyle>
    count?: number
    dotMargin?: number
    dotSize?: number
    easing?: EasingFunction
    interval?: number
  }

export const DotIndicator: FC<DotIndicatorProps> = ({
  animating,
  interval = 1000,
  easing = defaultEasing,
  color: backgroundColor = 'black',
  dotMargin = 4,
  dotSize = 9,
  count = 3,
  opacityEnabled = true,
  scaleEnabled = false,
  containerStyle,
  ...props
}) => {
  const { value } = useLoop({ animating, interval, easing })

  return (
    <Animated.View
      style={[styles.container, containerStyle]}
      {...props}
    >
      {useMemo<ReactNode>(() => {
        const dotStyle = {
          backgroundColor,
          marginHorizontal: dotMargin / 2,
          borderRadius: dotSize / 2,
          height: dotSize,
          width: dotSize
        }

        return range(count).map((_, index) => {
          const inputRange = getLoopInterpolateInputRange({ count })

          return (
            <Dot
              key={index}
              style={dotStyle}
              value={value}
              {...{ index, count, inputRange, scaleEnabled, opacityEnabled }}
            />
          )
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [backgroundColor, dotMargin, dotSize, count, scaleEnabled, opacityEnabled])}
    </Animated.View>
  )
}
